import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { useQueryClient } from '@tanstack/react-query'
import dayjs from '@/lib/dayjs'
import {
  getGetApiCompaniesCurrentServicemodulesCalendarsServiceModuleIdEventsQueryKey,
  getGetApiCompaniesCurrentServicemodulesCalendarsServiceModuleIdQueryKey,
  useGetApiCompaniesCurrentServicemodulesCalendarsServiceModuleId,
  useGetApiCompaniesCurrentServicemodulesCalendarsServiceModuleIdEvents,
  usePatchApiCompaniesCurrentServicemodulesCalendarsServiceModuleId,
} from '@/api/generated/company-management-user/company-management-user'
import type {
  CalendarServiceModuleAbsenceEventModel,
  CalendarServiceModuleEventModel,
  CalendarServiceModuleModel,
  ServiceModuleModel,
} from '@/api/model'
import { CalendarServiceModuleEventModelType } from '@/api/model/calendarServiceModuleEventModelType'
import { YearlyEventVisibility } from '@/api/model/yearlyEventVisibility'
import { Plus } from 'lucide-react'
import { Button } from '@/components/shadcn/button'
import { PageLayout } from '@/components/layout/PageLayout'
import { PageHeader } from '@/components/layout/PageHeader'
import { notify } from '@/lib/notify'
import { buildMonthGrid, dayKey } from './components/monthGrid'
import { MonthCalendar, type DayEvent } from './components/MonthCalendar'
import { YearlyVisibilityPicker } from './components/YearlyVisibilityPicker'
import { CalendarEventDialog } from './components/CalendarEventDialog'
import { GroupsVisibilityPicker } from './components/GroupsVisibilityPicker'
import { isServerUnreachable } from '@/lib/apiError'

interface Props {
  module: ServiceModuleModel
}

interface CalendarEntry {
  id: string
  label: string
  color: string
  start: string
  end: string
  onClick?: () => void
}

function getDayEventsFromCalendarEvent(
  event: CalendarServiceModuleEventModel,
  birthdayVis: YearlyEventVisibility | undefined,
  anniversaryVis: YearlyEventVisibility | undefined,
  t: TFunction,
  onEdit: (event: CalendarServiceModuleEventModel) => void
): CalendarEntry[] {
  const title = event.title ?? ''
  const years = (event as { years?: number }).years
  const color = event.colorHex || '#64748b'

  switch (event.eventType) {
    case CalendarServiceModuleEventModelType.Birthday:
      return [{
        id: event.id.toString(),
        color: color,
        label: `🎂 ${title}${birthdayVis === YearlyEventVisibility.VisibleWithYear && years ? ` (${years})` : ''}`.trim(),
        onClick: undefined,
        start: event.startDate,
        end: event.endDate,
      }];
    case CalendarServiceModuleEventModelType.Anniversary:
      return [{
        id: event.id.toString(),
        color: color,
        label: `🎉 ${title}${anniversaryVis === YearlyEventVisibility.VisibleWithYear && years ? ` (${years})` : ''}`.trim(),
        onClick: undefined,
        start: event.startDate,
        end: event.endDate,
      }];
    case CalendarServiceModuleEventModelType.Absence:
      const absenceEvent = event as CalendarServiceModuleAbsenceEventModel;
      const groups = (absenceEvent.groups ?? []).length > 0 ? absenceEvent.groups! : [{ name: "Group" }];

      return groups.map((e) => ({
        id: `${absenceEvent.employeeId}-${e.id}`,
        color: e.colorHex ?? color,
        label: t('calendar.absentLabel', { title: `${title} ${e.name}` }).trim(),
        onClick: undefined,
        start: event.startDate,
        end: event.endDate,
      }));
    default:
      return [{
        id: event.id.toString(),
        color: color,
        label: title,
        onClick: () => onEdit(event),
        start: event.startDate,
        end: event.endDate,
      }];
  }
}

/**
 * Calendar service module as its own page (card click → `?open`). Shows a
 * month calendar with the loaded events and, on the right, two persisting
 * radio filters for the visibility of birthdays/anniversaries. Appointments (`Basic`)
 * can be created via the header button or a click on a day, and edited/copied/deleted
 * by clicking an event (`CalendarEventDialog`).
 * Automatic events (birthday/anniversary/absence) are not editable.
 */
export function CalendarServiceModulePage({ module }: Props) {
  const { t } = useTranslation('service-modules-calendar')
  const id = module.id
  const queryClient = useQueryClient()

  const [month, setMonth] = useState(() => dayjs().startOf('month'))
  const range = useMemo(() => buildMonthGrid(month), [month])

  const { data: moduleData } = useGetApiCompaniesCurrentServicemodulesCalendarsServiceModuleId(id)
  const detail = ((moduleData as { data?: CalendarServiceModuleModel })?.data ??
    moduleData) as CalendarServiceModuleModel | undefined

  // Local radio state: reacts immediately (optimistic), the source of truth stays the module.
  const [birthdayVis, setBirthdayVis] = useState<YearlyEventVisibility>()
  const [anniversaryVis, setAnniversaryVis] = useState<YearlyEventVisibility>()
  useEffect(() => {
    if (detail) {
      setBirthdayVis(detail.birthdayVisibility)
      setAnniversaryVis(detail.anniversaryVisibility)
    }
  }, [detail])

  const [visibleGroups, setVisibleGroups] = useState<number[]>([])

  const eventsQuery = useGetApiCompaniesCurrentServicemodulesCalendarsServiceModuleIdEvents(id, {
    StartDate: range.gridStart.startOf('day').toISOString(),
    EndDate: range.gridEnd.endOf('day').toISOString(),
    GroupIds: visibleGroups,
  })

  useEffect(() => {
    if(!eventsQuery.error) {
      return
    }

    if (isServerUnreachable(eventsQuery.error)) {
      notify.error(t('serverUnreachable'))
      return
    }
    notify.apiError(eventsQuery.error, t('eventsLoadError'))
  }, [eventsQuery.error])

  const events = useMemo(
    () =>
      ((eventsQuery.data as { data?: CalendarServiceModuleEventModel[] })?.data ??
        eventsQuery.data ??
        []) as CalendarServiceModuleEventModel[],
    [eventsQuery.data],
  )

  // Appointment dialog (create/edit). Empty object (without id) = create.
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogEvent, setDialogEvent] = useState<Partial<CalendarServiceModuleEventModel>>({})

  const openCreate = useCallback((day?: dayjs.Dayjs) => {
    const d = (day ?? dayjs()).format('YYYY-MM-DD')
    setDialogEvent({ startDate: d, endDate: d, isCompanyWideAssigned: true })
    setDialogOpen(true)
  }, [])

  const openEdit = useCallback((event: CalendarServiceModuleEventModel) => {
    setDialogEvent(event)
    setDialogOpen(true)
  }, [])

  // Copy: build a new event from the opened one (+1 day, without reminders);
  // the dialog stays open and re-seeds as create through the event change.
  const handleCopy = useCallback(() => {
    setDialogEvent((src) => ({
      ...src,
      id: undefined,
      title: `${src.title ?? ''} Copy`,
      startDate: src.startDate ? dayjs.utc(src.startDate).add(1, 'day').toISOString() : undefined,
      endDate: src.endDate ? dayjs.utc(src.endDate).add(1, 'day').toISOString() : undefined,
      reminderNotifications: [],
      sendUpdateNotification: false,
    }))
  }, [])

  // Distribute events across their days (multi-day events appear in each cell).
  const eventsByDay = useMemo(() => {
    const map = new Map<string, DayEvent[]>()
    for (const calendarEvent of events) {
      // One event may result in several calendar entries (depending on the event type)
      const finalEvents = getDayEventsFromCalendarEvent(
        calendarEvent,
        birthdayVis,
        anniversaryVis,
        t,
        openEdit
      );

      for (const event of finalEvents) {
        const last = dayjs(event.end).startOf('day')
        let day = dayjs(event.start).startOf('day')
        let guard = 0
        while ((day.isBefore(last) || day.isSame(last, 'day')) && guard < 366) {
          const key = dayKey(day)
          const list = map.get(key) ?? []

          list.push({
            key: `${event.id}-${key}`,
            label: event.label,
            color: event.color,
            onClick: event.onClick
          })

          map.set(key, list)
          day = day.add(1, 'day')
          guard++
        }
      }
    }
    return map
  }, [events, birthdayVis, anniversaryVis, t, openEdit])

  const patch = usePatchApiCompaniesCurrentServicemodulesCalendarsServiceModuleId()

  async function changeVisibility(
    field: 'birthdayVisibility' | 'anniversaryVisibility',
    value: YearlyEventVisibility,
  ) {
    if (!detail) return
    const prevBirthday = birthdayVis
    const prevAnniversary = anniversaryVis
    const nextBirthday = field === 'birthdayVisibility' ? value : birthdayVis
    const nextAnniversary = field === 'anniversaryVisibility' ? value : anniversaryVis
    setBirthdayVis(nextBirthday)
    setAnniversaryVis(nextAnniversary)

    try {
      await patch.mutateAsync({
        serviceModuleId: id,
        data: {
          name: detail.name ?? null,
          iconName: detail.iconName ?? null,
          colorHex: detail.colorHex ?? null,
          sendNotification: false,
          isVisible: detail.isVisible,
          isCompanyWideAssigned: detail.isCompanyWideAssigned,
          assignedGroupIds: detail.assignedGroupIds ?? [],
          birthdayVisibility: nextBirthday,
          anniversaryVisibility: nextAnniversary,
        },
      })
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: getGetApiCompaniesCurrentServicemodulesCalendarsServiceModuleIdQueryKey(id),
        }),
        queryClient.invalidateQueries({
          queryKey: getGetApiCompaniesCurrentServicemodulesCalendarsServiceModuleIdEventsQueryKey(id),
        }),
      ])
    } catch (error) {
      setBirthdayVis(prevBirthday)
      setAnniversaryVis(prevAnniversary)
      notify.apiError(error, t('calendar.visibilityError'))
    }
  }

  return (
    <PageLayout scrollEnabled>
      <PageHeader title={module.name ?? ''} description={t('calendar.description')}>
        <Button onClick={() => openCreate()}>
          <Plus className="size-4" />
          {t('event.create')}
        </Button>
      </PageHeader>

      <div className="grid gap-4 lg:grid-cols-[1fr_260px]">
        <MonthCalendar
          month={month}
          eventsByDay={eventsByDay}
          isLoading={eventsQuery.isLoading || eventsQuery.isFetching}
          onPrev={() => setMonth((m) => m.subtract(1, 'month'))}
          onNext={() => setMonth((m) => m.add(1, 'month'))}
          onToday={() => setMonth(dayjs().startOf('month'))}
          onSelectDay={openCreate}
        />

        <div className="flex flex-col gap-6 rounded-xl border bg-card p-4">
          <YearlyVisibilityPicker
            title={t('calendar.birthdays')}
            value={birthdayVis}
            onChange={(v) => changeVisibility('birthdayVisibility', v)}
            disabled={!detail || patch.isPending}
          />
          <YearlyVisibilityPicker
            title={t('calendar.anniversaries')}
            value={anniversaryVis}
            onChange={(v) => changeVisibility('anniversaryVisibility', v)}
            disabled={!detail || patch.isPending}
          />
          <GroupsVisibilityPicker
            visibleGroups={visibleGroups}
            onChange={setVisibleGroups}
            disabled={!detail || patch.isPending}
          />
        </div>
      </div>

      <CalendarEventDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        serviceModuleId={id}
        hasSurveyAppointmentSubtypes={detail?.hasSurveyAppointmentSubtypes ?? false}
        event={dialogEvent}
        onCopy={handleCopy}
      />
    </PageLayout>
  )
}
